import { SlideDeckMaterialType } from './schema'

// Shared by the browser upload helpers and the server JSON importer. Kept out
// of index.ts, which pulls in the client Storage SDK the server cannot load.

type FileExtensionWithMimeType = {
  readonly extension: string
  readonly mimeType: string
}

type UploadFieldMapping = {
  readonly materialLink: readonly FileExtensionWithMimeType[]
  readonly imageUrl?: readonly FileExtensionWithMimeType[]
}

export const slideDeckMaterialUploadFieldsWithExtensionsFromMaterialType = (
  materialType: SlideDeckMaterialType
): UploadFieldMapping => {
  const mp3 = {
    extension: 'mp3',
    mimeType: 'audio/mpeg',
  } as const
  const mp4 = {
    extension: 'mp4',
    mimeType: 'video/mp4',
  } as const
  const jpg = {
    extension: 'jpg',
    mimeType: 'image/jpeg',
  } as const
  const jpeg = {
    extension: 'jpeg',
    mimeType: 'image/jpeg',
  } as const
  const png = {
    extension: 'png',
    mimeType: 'image/png',
  } as const
  const webm = {
    extension: 'webm',
    mimeType: 'video/webm',
  } as const
  const pdf = {
    extension: 'pdf',
    mimeType: 'application/pdf',
  } as const

  // record with every key of SlideDeckMaterialType
  const uploadableTypesForFieldByType: Record<
    SlideDeckMaterialType,
    UploadFieldMapping
  > = {
    [SlideDeckMaterialType.mp3]: {
      materialLink: [mp3],
      imageUrl: [jpg, jpeg, png],
    },
    [SlideDeckMaterialType.pdf]: {
      materialLink: [pdf],
    },
    [SlideDeckMaterialType.webm]: {
      materialLink: [webm],
    },
    [SlideDeckMaterialType.mp4]: {
      materialLink: [mp4],
    },
    [SlideDeckMaterialType.featuredLarge]: {
      materialLink: [jpg, jpeg, png],
    },
    [SlideDeckMaterialType.img]: {
      materialLink: [jpg, jpeg, png],
    },
    [SlideDeckMaterialType.featuredSquare]: {
      materialLink: [jpg, jpeg, png],
    },
    [SlideDeckMaterialType.url]: {
      materialLink: [],
    },
  }

  return uploadableTypesForFieldByType[materialType]
}

// The Go material trigger reads these keys and crops/scales the uploaded
// object in place, so the ratio here must match what the client validated.
export function getCustomMetadataForFieldAndMaterialType({
  mimeType,
  field,
  materialType,
}: {
  mimeType: string
  field: string
  materialType: SlideDeckMaterialType
}) {
  // only add custom metadata for image mime types
  if (!mimeType.startsWith('image/')) return null

  // only used by podcasts, safely set to aspect ratio 1:1
  if (field === 'imageUrl')
    return {
      scaleToDimension: '3000', // recommended dimension for podcast cover art
      cropToAspectRatio: '1', // 1:1 aspect ratio
    }

  if (materialType === SlideDeckMaterialType.featuredSquare)
    return {
      scaleToDimension: '1080', // catalog cards render the square small
      cropToAspectRatio: '1', // 1:1 aspect ratio
    }

  // all other images should be 16:9
  return {
    scaleToDimension: '1920', // 1920px width for material images
    cropToAspectRatio: '1.777777777', // 16:9 aspect ratio
  }
}
